/**
 * Warengruppen verwalten.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatAmount, today } from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Dialog,
  EmptyState,
  Field,
  LoadingBlock,
  TextArea,
  TextInput,
} from '../components/ui';

export type Category = {
  id: string;
  number: string | null;
  name: string;
  description: string | null;
  revenueAccount: string | null;
  expenseAccount: string | null;
  customsTariffNumber: string | null;
  countryOfOrigin: string | null;
  sortOrder: number;
  active: boolean;
  itemCount: number;
};

type RevenueGroup = {
  categoryId: string | null;
  categoryName: string;
  categoryNumber: string | null;
  net: number;
  quantity: number;
  share: number;
};

export function CategoriesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayWrite = can('masterdata:write');

  const [editing, setEditing] = useState<Category | 'neu' | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () =>
      api.get<{ categories: Category[]; unassignedCount: number }>('/api/categories'),
  });

  const yearStart = `${new Date().getFullYear()}-01-01`;
  const { data: revenue } = useQuery({
    queryKey: ['category-revenue', yearStart],
    queryFn: () =>
      api.get<{ total: number; groups: RevenueGroup[] }>(
        withQuery('/api/categories/revenue', { from: yearStart, to: today() }),
      ),
    enabled: can('sales:read'),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: boolean; message?: string }>(`/api/categories/${id}`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast[result.deleted ? 'success' : 'info'](
        result.message ?? 'Die Warengruppe wurde gelöscht.',
      );
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.'),
  });

  if (isLoading || !data) return <LoadingBlock />;

  const revenueByCategory = new Map(
    (revenue?.groups ?? []).map((group) => [group.categoryId, group]),
  );

  return (
    <>
      <Alert kind="info">
        Warengruppen ordnen den Artikelstamm und erlauben die Umsatzauswertung nach Sortiment.
        Ist an einer Gruppe ein Erlöskonto hinterlegt, buchen alle Artikel der Gruppe darauf —
        sonst gilt die Vorgabe aus dem Kontenrahmen.
      </Alert>

      {data.unassignedCount > 0 ? (
        <Alert kind="warning">
          {data.unassignedCount} {data.unassignedCount === 1 ? 'Artikel ist' : 'Artikel sind'}
          {' '}keiner Warengruppe zugeordnet und {data.unassignedCount === 1 ? 'fällt' : 'fallen'}
          {' '}in der Auswertung unter „Ohne Warengruppe".
        </Alert>
      ) : null}

      <div className="card">
        <div className="card-header">
          <h2>{data.categories.length} Warengruppen</h2>
          {mayWrite ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('neu')}>
              Warengruppe anlegen
            </button>
          ) : null}
        </div>

        {data.categories.length === 0 ? (
          <EmptyState title="Noch keine Warengruppen" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Kürzel</th>
                  <th>Bezeichnung</th>
                  <th className="shrink">Erlöskonto</th>
                  <th className="numeric">Artikel</th>
                  <th className="numeric">Umsatz {new Date().getFullYear()}</th>
                  <th className="numeric">Anteil</th>
                  <th className="shrink"></th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map((category) => {
                  const group = revenueByCategory.get(category.id);

                  return (
                    <tr key={category.id}>
                      <td className="mono">{category.number ?? ''}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{category.name}</div>
                        {category.description ? (
                          <div className="faint">{category.description}</div>
                        ) : null}
                        {!category.active ? <Badge kind="warning">Inaktiv</Badge> : null}
                      </td>
                      <td className="mono muted">{category.revenueAccount ?? ''}</td>
                      <td className="numeric">{category.itemCount}</td>
                      <td className="numeric">{group ? formatAmount(group.net) : ''}</td>
                      <td className="numeric muted">
                        {group && group.share > 0 ? `${group.share.toFixed(1)} %` : ''}
                      </td>
                      <td>
                        {mayWrite ? (
                          <div className="row" style={{ gap: 6 }}>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => setEditing(category)}
                            >
                              Bearbeiten
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => remove.mutate(category.id)}
                            >
                              Löschen
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}

                {revenueByCategory.has(null) ? (
                  <tr>
                    <td />
                    <td className="faint">Ohne Warengruppe</td>
                    <td />
                    <td />
                    <td className="numeric">{formatAmount(revenueByCategory.get(null)!.net)}</td>
                    <td className="numeric muted">
                      {revenueByCategory.get(null)!.share.toFixed(1)} %
                    </td>
                    <td />
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <CategoryDialog
          category={editing === 'neu' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function CategoryDialog({
  category,
  onClose,
}: {
  category: Category | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    number: category?.number ?? '',
    name: category?.name ?? '',
    description: category?.description ?? '',
    revenueAccount: category?.revenueAccount ?? '',
    expenseAccount: category?.expenseAccount ?? '',
    customsTariffNumber: category?.customsTariffNumber ?? '',
    countryOfOrigin: category?.countryOfOrigin ?? '',
    sortOrder: category?.sortOrder ?? 0,
    active: category?.active ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        number: form.number || null,
        description: form.description || null,
        revenueAccount: form.revenueAccount || null,
        expenseAccount: form.expenseAccount || null,
        customsTariffNumber: form.customsTariffNumber || null,
        countryOfOrigin: form.countryOfOrigin || null,
      };
      return category
        ? api.patch(`/api/categories/${category.id}`, payload)
        : api.post('/api/categories', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Gespeichert.');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        if (Object.keys(error.fieldErrors).length === 0) toast.error(error.message);
      } else {
        toast.error('Speichern fehlgeschlagen.');
      }
    },
  });

  return (
    <Dialog
      title={category ? `Warengruppe ${category.name}` : 'Warengruppe anlegen'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !form.name.trim()}
            onClick={() => {
              setErrors({});
              save.mutate();
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Kürzel" span={4} error={errors.number} hint="z. B. KAB">
          <TextInput
            value={form.number}
            onChange={(value) => setForm({ ...form, number: value.toUpperCase() })}
            invalid={Boolean(errors.number)}
          />
        </Field>
        <Field label="Reihenfolge" span={4} hint="kleinere Zahl zuerst">
          <input autoComplete="off"
            className="input numeric"
            type="number"
            min={0}
            value={form.sortOrder}
            onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) || 0 })}
          />
        </Field>
        <Field label="Bezeichnung" span={12} required error={errors.name}>
          <TextInput
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
            invalid={Boolean(errors.name)}
            autoFocus
          />
        </Field>
        <Field label="Beschreibung" span={12}>
          <TextArea
            value={form.description}
            onChange={(value) => setForm({ ...form, description: value })}
            rows={2}
          />
        </Field>
        <Field
          label="Erlöskonto"
          span={6}
          error={errors.revenueAccount}
          hint="leer = Vorgabe aus dem Kontenrahmen"
        >
          <TextInput
            value={form.revenueAccount}
            onChange={(value) => setForm({ ...form, revenueAccount: value })}
          />
        </Field>
        <Field label="Wareneinsatzkonto" span={6} error={errors.expenseAccount}>
          <TextInput
            value={form.expenseAccount}
            onChange={(value) => setForm({ ...form, expenseAccount: value })}
          />
        </Field>
        <Field
          label="Warennummer (Vorgabe)"
          span={6}
          error={errors.customsTariffNumber}
          hint="8 Ziffern; gilt für Artikel der Gruppe ohne eigene Warennummer"
        >
          <TextInput
            value={form.customsTariffNumber}
            onChange={(value) => setForm({ ...form, customsTariffNumber: value })}
            maxLength={8}
          />
        </Field>
        <Field label="Ursprungsland (Vorgabe)" span={6} error={errors.countryOfOrigin} hint="Ländercode, z. B. DE">
          <TextInput
            value={form.countryOfOrigin}
            onChange={(value) => setForm({ ...form, countryOfOrigin: value.toUpperCase() })}
            maxLength={2}
          />
        </Field>
      </div>

      {category ? (
        <label className="checkbox-row" style={{ marginTop: 14 }}>
          <input
            type="checkbox"
            checked={form.active}
            onChange={(event) => setForm({ ...form, active: event.target.checked })}
          />
          Aktiv (erscheint in Auswahllisten)
        </label>
      ) : null}
    </Dialog>
  );
}
